import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, X, ExternalLink, AlertTriangle, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { EventBadges, ConfidenceBadge } from "@/components/CatalystBadges";
import { fmtAgo, isTbd } from "@/lib/catalysts";

const fmtDay = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Date TBD");

export default function ReviewQueue({ open, onOpenChange, onChanged, onEdit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/catalysts/pending"); setRows(data); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const act = async (e, action) => {
    setBusy(e.id);
    try {
      const { data } = await api.post(`/catalysts/${e.id}/${action}`);
      toast.success(action === "approve" ? (data.mergedInto ? "Merged into the existing event" : "Approved — now live") : "Rejected");
      setRows((r) => r.filter((x) => x.id !== e.id));
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-[#334566] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="review-queue-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Review queue <span className="text-slate-500 font-mono text-base">({rows.length})</span></DialogTitle>
          <DialogDescription className="text-slate-400">News-scan finds, community tips and AI drafts. Approve to publish, reject to hide (rejected items won't come back).</DialogDescription>
        </DialogHeader>
        {loading && <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-400" /></div>}
        {!loading && rows.length === 0 && <div className="py-12 text-center text-slate-500" data-testid="review-empty">Queue is clear.</div>}
        <div className="space-y-2">
          {rows.map((e) => (
            <div key={e.id} className="rounded-xl border border-[#222F43] bg-[#111723] p-3" data-testid={`review-${e.id}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{e.title}</div>
                  <div className="text-[11px] font-mono text-slate-500 mt-0.5">
                    {isTbd(e) ? <span className="text-purple-300">Date TBD{e.dateHint ? ` · ${e.dateHint}` : ""}</span> : fmtDay(e.date)}{e.time ? ` · ${e.time}` : ""} · {fmtAgo(e.created_at)}
                    {e.submittedBy && <> · by {e.submittedBy}</>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mt-1.5"><EventBadges e={e} /><ConfidenceBadge e={e} /></div>
                  {e.description && <p className="text-xs text-slate-300 mt-1.5">{e.description}</p>}
                  {e.duplicateOf && (
                    <div className="mt-1.5 text-xs text-amber-300 flex items-center gap-1.5" data-testid={`review-dup-${e.id}`}>
                      <AlertTriangle className="h-3.5 w-3.5" /> Possible duplicate of “{e.duplicateOf.title}” ({fmtDay(e.duplicateOf.date)}). Approve = merge the link into it.
                    </div>
                  )}
                  {(e.sources || []).slice(0, 2).map((s, i) => (
                    <a key={i} href={s.url} target="_blank" rel="noreferrer" className="text-[11px] text-cyan-400 hover:underline flex items-center gap-1 mt-1"><ExternalLink className="h-3 w-3" /> {s.name}{s.title ? ` — ${s.title.slice(0, 70)}` : ""}</a>
                  ))}
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <Button size="sm" onClick={() => act(e, "approve")} disabled={busy === e.id} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold h-8" data-testid={`review-approve-${e.id}`}><Check className="h-3.5 w-3.5 mr-1" /> {e.duplicateOf ? "Merge" : "Approve"}</Button>
                  <Button size="sm" variant="outline" onClick={() => onEdit?.(e)} className="border-[#334566] bg-transparent h-8" data-testid={`review-edit-${e.id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => act(e, "reject")} disabled={busy === e.id} className="border-red-500/40 text-red-400 bg-transparent h-8" data-testid={`review-reject-${e.id}`}><X className="h-3.5 w-3.5 mr-1" /> Reject</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
